import { useId, useState } from 'react';
import { ArrowMark, ButtonLink } from '../components/Shell';
import { Badge, PhoneFrame, QrPattern, Screen } from '../mock/primitives';
import { useContent } from '../siteContent';

const deviceClass = ['mx-device-desktop', 'mx-device-tablet', 'mx-device-phone'];

export function Experience() {
  const { experience, settings } = useContent();
  if (!experience.visible && !experience.devicesVisible) return null;
  return <section className="mx-experience" aria-labelledby={experience.visible ? 'experience-title' : undefined}>
    {experience.visible && <div className="mx-container mx-experience-grid">
      <div className="mx-experience-visual">
        <PhoneFrame className="mx-exp-phone">
          <Screen label="تجربة العميل: بطاقة الاشتراك والرصيد والحالة على الجوال" className="mk-phone-screen mk-customer">
            <div className="mk-ph-head"><small>مرحبًا سارة</small><b>اشتراكي</b></div>
            <div className="mk-subcard mk-subcard-sm"><div className="mk-subcard-top"><span><small>مطعمك</small><b>باقة الشهر</b></span><Badge status="active" /></div><QrPattern /><div className="mk-subcard-meta"><span><small>الرصيد</small><b>51 وجبة</b></span><span><small>ينتهي بعد</small><b>18 يومًا</b></span></div></div>
            <div className="mk-notice"><b>تم تسجيل صرف وجبتك</b><small>الفرع الرئيسي · الآن</small></div>
          </Screen>
        </PhoneFrame>
        <div className="mx-app-badge">
          <span><b>{experience.appTitle}</b><small>{experience.appText}</small></span>
          {settings.appStoreUrl && <a className="mx-btn mx-btn-quiet mx-btn-sm" href={settings.appStoreUrl} target="_blank" rel="noopener">حمّل من App Store</a>}
        </div>
      </div>
      <div className="mx-experience-copy">
        <h2 id="experience-title" className="mx-h2"><span>{experience.line1}</span><span>{experience.line2}</span></h2>
        <p className="mx-body">{experience.body}</p>
        <dl className="mx-defs">{experience.points.map((point, index) => <div key={index}><dt>{point.title}</dt><dd>{point.text}</dd></div>)}</dl>
        {experience.statement && <p className="mx-statement mx-statement-sm">{experience.statement}</p>}
      </div>
    </div>}
    {experience.devicesVisible && <div className={`mx-container mx-devices${experience.visible ? '' : ' mx-devices-solo'}`}>
      <h2 className="mx-h2 mx-h2-sm"><span>{experience.devicesLine1}</span><span>{experience.devicesLine2}</span></h2>
      <p className="mx-body">{experience.devicesBody}</p>
      <ul className="mx-device-row" aria-label="الأجهزة المدعومة">
        {experience.devices.slice(0, 3).map((label, index) => <li key={index}><span className={`mx-device ${deviceClass[index]}`} aria-hidden="true" /><b>{label}</b></li>)}
      </ul>
      {experience.devicesNote && <p className="mx-body mx-center">{experience.devicesNote}</p>}
    </div>}
  </section>;
}

export function Security() {
  const { security } = useContent();
  if (!security.visible) return null;
  return <section className="mx-security" aria-labelledby="security-title">
    <div className="mx-container mx-security-grid">
      <div>
        <h2 id="security-title" className="mx-h2"><span>{security.line1}</span><span>{security.line2}</span></h2>
        <p className="mx-body">{security.body}</p>
      </div>
      <div>
        <ul className="mx-ruled">{security.points.map((point, index) => <li key={index}>{point}</li>)}</ul>
        {security.statement && <p className="mx-statement mx-statement-sm">{security.statement}</p>}
      </div>
    </div>
  </section>;
}

export function Pricing() {
  const { pricing, settings } = useContent();
  if (!pricing.visible) return null;
  return <section id="pricing" className="mx-pricing" aria-labelledby="pricing-title">
    <div className="mx-container">
      <header className="mx-section-head mx-center">
        <h2 id="pricing-title" className="mx-h2"><span>{pricing.line1}</span><span>{pricing.line2}</span></h2>
        <p className="mx-body">{pricing.body}</p>
      </header>
      <article className="mx-price-card" aria-labelledby="plan-name">
        <div className="mx-price-main">
          <h3 id="plan-name">{pricing.planName}</h3>
          <p className="mx-price"><b>{pricing.price}</b><span>{pricing.period}<small>{pricing.unit}</small></span></p>
          <p className="mx-price-sub">{pricing.subtitle}</p>
          {pricing.setupFee && <p className="mx-setup">{pricing.setupLabel} <b>{pricing.setupFee}</b></p>}
          <div className="mx-actions">
            <ButtonLink href={settings.primaryCta} arrow>{pricing.primaryCta}</ButtonLink>
            {pricing.secondaryCta && <ButtonLink href="/support" variant="quiet">{pricing.secondaryCta}</ButtonLink>}
          </div>
        </div>
        <div className="mx-price-list">
          <h4>{pricing.includedTitle}</h4>
          <ul className="mx-ticks mx-ticks-2">{pricing.included.map((item, index) => <li key={index}>{item}</li>)}</ul>
        </div>
      </article>
      {pricing.fineprint && <p className="mx-fineprint">{pricing.fineprint}</p>}
    </div>
  </section>;
}

export function Faq({ id = 'faq', heading = true }: { id?: string; heading?: boolean }) {
  const { faq } = useContent();
  const [open, setOpen] = useState<number | null>(0);
  const base = useId();
  if (!faq.items.length || (heading && !faq.visible)) return null;
  return <section id={id} className="mx-faq" aria-labelledby={heading ? `${base}-title` : undefined} aria-label={heading ? undefined : 'الأسئلة الشائعة'}>
    <div className="mx-container mx-faq-grid">
      {heading && <h2 id={`${base}-title`} className="mx-h2"><span>{faq.line1}</span><span>{faq.line2}</span></h2>}
      <div className="mx-accordion">
        {faq.items.map((item, index) => { const expanded = open === index; return <div key={index} className={`mx-acc${expanded ? ' is-open' : ''}`}>
          <h3><button type="button" id={`${base}-q${index}`} aria-expanded={expanded} aria-controls={`${base}-a${index}`} onClick={() => setOpen(expanded ? null : index)}>{item.q}<span className="mx-acc-icon" aria-hidden="true" /></button></h3>
          <div id={`${base}-a${index}`} role="region" aria-labelledby={`${base}-q${index}`} className="mx-acc-panel"><div><p>{item.a}</p></div></div>
        </div>; })}
      </div>
    </div>
  </section>;
}

export function FinalCta() {
  const { final, settings } = useContent();
  if (!final.visible) return null;
  return <section className="mx-final" aria-labelledby="final-title">
    <div className="mx-container mx-center">
      <h2 id="final-title" className="mx-display mx-display-sm"><span>{final.line1}</span><span className="mx-green">{final.line2}</span></h2>
      {final.lead && <p className="mx-lead">{final.lead}</p>}
      <div className="mx-actions mx-actions-center"><ButtonLink href={settings.primaryCta} arrow>{final.cta}</ButtonLink></div>
      {final.signoff && <p className="mx-signoff"><ArrowMark className="mx-arrow mx-arrow-brand" />{final.signoff}</p>}
    </div>
  </section>;
}
